import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Key } from "@mui/icons-material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { updateParentPassword as updateParentPasswordApi } from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import PasswordHints from "components/PasswordHints";

interface Props {
  open: boolean;
  onClose: () => void;
  item: { id: number; name: string };
}

type FormFields = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const schema = Yup.object().shape({
    password: Yup.string()
      .required(t("inputs.password") + " required")
      .min(6, "Min 6 characters"),
    confirmPassword: Yup.string()
      .required(t("inputs.confirmPassword") + " required")
      .oneOf([Yup.ref("password")], "Passwords do not match"),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormFields>({ resolver: yupResolver(schema) });

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    setSubmitting(true);
    updateParentPasswordApi({ id: item.id, password: data.password })
      .then(() => {
        enqueueSnackbar(t("parents.password_reset"), { variant: "success" });
        onClose();
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Error", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Key sx={{ fontSize: 22 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {t("labels.reset_password")}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {item.name}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <TextField
            label={t("inputs.newPassword")}
            type="password"
            fullWidth
            size="small"
            autoFocus
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            {...register("password")}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <PasswordHints value={watch("password")} sx={{ mb: 2 }} />
          <TextField
            label={t("inputs.confirmPassword")}
            type="password"
            fullWidth
            size="small"
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword?.message}
            {...register("confirmPassword")}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.update")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default ResetPasswordDialog;
